<template>
  <nav aria-label="Breadcrumb navigation" class="modern-breadcrumb mb-4">
    <div class="breadcrumb-container">
      <Breadcrumb
        :model="breadcrumbItems"
        class="modern-breadcrumb-inner"
        separator="pi pi-chevron-right"
      />
    </div>
  </nav>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import Breadcrumb from 'primevue/breadcrumb';

// Props to receive breadcrumb items
const props = defineProps({
  items: {
    type: Array,
    required: true,
    default: () => []
  }
});

// Get router instance once
const router = useRouter();

// Transform props to PrimeVue breadcrumb format
const breadcrumbItems = computed(() => {
  return props.items.map(item => ({
    label: item.label,
    icon: item.icon,
    route: item.route,
    command: item.route ? () => {
      router.push(item.route);
    } : null
  }));
});
</script>

<script>
export default {
  name: 'ModernBreadcrumb'
};
</script>

<style scoped>
/* Modern Breadcrumb Styles - Mobile First (iPhone SE 375px optimized) */
.modern-breadcrumb {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem; /* Mobile: smaller radius */
  padding: 0.5rem 0.75rem; /* Mobile: compact padding */
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  overflow: hidden;
}

.breadcrumb-container {
  width: 100%;
  overflow: hidden;
}

.modern-breadcrumb :deep(.p-breadcrumb) {
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  box-shadow: none !important;
}

.modern-breadcrumb :deep(.p-breadcrumb-list) {
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  flex-wrap: nowrap !important;
  overflow: hidden !important;
}

.modern-breadcrumb :deep(.p-menuitem) {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.modern-breadcrumb :deep(.p-menuitem:not(:last-child)) {
  max-width: 90px; /* Mobile: very compact for iPhone SE */
  overflow: hidden;
}

.modern-breadcrumb :deep(.p-menuitem:last-child) {
  flex-shrink: 1;
  min-width: 0;
}

/* Mobile First - Base styles optimized for iPhone SE (375px) */
.modern-breadcrumb :deep(.p-menuitem-link) {
  display: flex !important;
  align-items: center !important;
  gap: 0.25rem !important; /* Mobile: smaller gap */
  padding: 0.125rem 0.25rem !important; /* Mobile: compact padding */
  border-radius: 0 !important;
  font-size: 0.75rem !important; /* Mobile: smaller text */
  font-weight: 500 !important;
  transition: all 0.2s ease !important;
  color: #4a89dc !important;
  text-decoration: none !important;
  background: transparent !important;
  border: none !important;
}

.modern-breadcrumb :deep(.p-menuitem-link .p-menuitem-text) {
  color: #4a89dc !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  max-width: 80px !important; /* Mobile: very compact */
}

.modern-breadcrumb :deep(.p-menuitem:last-child .p-menuitem-link .p-menuitem-text) {
  color: #64748b !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  max-width: 100px !important; /* Mobile: current item slightly larger */
}

.modern-breadcrumb :deep(.p-menuitem-link .p-menuitem-icon) {
  color: #4a89dc !important;
  flex-shrink: 0 !important;
  font-size: 0.75rem !important; /* Mobile: smaller icons */
}

.modern-breadcrumb :deep(.p-menuitem-link:hover) {
  color: #5ca0f2 !important;
  background: transparent !important;
  transform: none !important;
  box-shadow: none !important;
}

.modern-breadcrumb :deep(.p-menuitem:last-child .p-menuitem-link) {
  color: #64748b !important;
  background: transparent !important;
  border: none !important;
  pointer-events: none !important;
  font-weight: 600 !important;
}


.modern-breadcrumb :deep(.p-menuitem:last-child .p-menuitem-link .p-menuitem-icon) {
  color: #64748b !important;
}

.modern-breadcrumb :deep(.p-menuitem-separator) {
  color: #9ca3af !important;
  font-size: 0.75rem !important; /* Mobile: smaller separator */
  margin: 0 0.125rem !important; /* Mobile: compact margin */
}

/* ===== SMALL TABLET (576px+) ===== */
@media (min-width: 576px) {
  .modern-breadcrumb {
    padding: 0.75rem 1rem;
  }

  .modern-breadcrumb :deep(.p-menuitem-link) {
    gap: 0.375rem !important;
    padding: 0.25rem 0.375rem !important;
    font-size: 0.8125rem !important;
  }

  .modern-breadcrumb :deep(.p-menuitem-link .p-menuitem-text) {
    max-width: 120px !important;
  }

  .modern-breadcrumb :deep(.p-menuitem:last-child .p-menuitem-link .p-menuitem-text) {
    max-width: 140px !important;
  }

  .modern-breadcrumb :deep(.p-menuitem:not(:last-child)) {
    max-width: 140px;
  }

  .modern-breadcrumb :deep(.p-menuitem-separator) {
    font-size: 0.8125rem !important;
    margin: 0 0.1875rem !important;
  }
}

/* ===== MEDIUM TABLET (768px+) ===== */
@media (min-width: 768px) {
  .modern-breadcrumb {
    border-radius: 0.75rem;
    padding: 0.75rem 1rem;
  }

  .modern-breadcrumb :deep(.p-menuitem-link) {
    gap: 0.5rem !important;
    font-size: 0.875rem !important;
  }

  .modern-breadcrumb :deep(.p-menuitem-link .p-menuitem-text) {
    max-width: 150px !important;
  }

  .modern-breadcrumb :deep(.p-menuitem:last-child .p-menuitem-link .p-menuitem-text) {
    max-width: 200px !important;
  }

  .modern-breadcrumb :deep(.p-menuitem:not(:last-child)) {
    max-width: 180px;
  }

  .modern-breadcrumb :deep(.p-menuitem-separator) {
    font-size: 0.875rem !important;
    margin: 0 0.25rem !important;
  }
}

/* ===== LARGE DESKTOP (992px+) ===== */
@media (min-width: 992px) {
  .modern-breadcrumb {
    padding: 1rem 1.25rem;
  }

  .modern-breadcrumb :deep(.p-menuitem-link .p-menuitem-text) {
    max-width: 200px !important;
  }

  .modern-breadcrumb :deep(.p-menuitem:last-child .p-menuitem-link .p-menuitem-text) {
    max-width: 250px !important;
  }

  .modern-breadcrumb :deep(.p-menuitem:not(:last-child)) {
    max-width: 220px;
  }
}
</style>